@if (settingsLoading()) {
	<div class="admin-feedback">
		<div class="status-text">Loading admin settings...</div>
	</div>
}
@if (settingsError()) {
	<div class="admin-feedback">
		<div class="error-banner" role="alert">
			<i class="bi bi-exclamation-octagon-fill"></i>
			<span class="error-banner-message">{{ settingsError() }}</span>
			<button
				type="button"
				class="error-banner-dismiss"
				(click)="clearSettingsError()"
				aria-label="Dismiss error"
			>
				✕
			</button>
		</div>
	</div>
}
<div class="admin-shell">
	<div class="admin-panels-row">
		<!-- STREAM PANEL -->
		<section class="admin-panel">
			<h2 class="card-title flex items-center gap-2">
				<div class="header-icon">
					<i class="bi bi-camera-video"></i>
				</div>
				Stream Panel
			</h2>
			<div class="divider mb-4"></div>
			@if (settings()) {
				<div class="flex items-center justify-between">
					<div class="flex flex-col">
						<span>Video Stream</span>
						<span class="text-xs text-[var(--text-secondary)]"
							>Enable or disable the live main/driver feed
						</span>
						@if (settings()?.race?.disableStream) {
							<div class="stream-status-inactive text-xs p-1 rounded mt-3">
								<strong>• STREAM INACTIVE</strong>
							</div>
						} @else {
							<div class="stream-status-active text-xs p-1 rounded mt-3">
								<strong>• STREAM ACTIVE</strong>
							</div>
						}
					</div>
					<vb-lxp-switch
						[checked]="driverStreamStatus()"
						[saveFn]="saveDriverStreamStatus"
					></vb-lxp-switch>
				</div>
				<div class="flex items-center gap-4 mt-6">
					<div class="divider"></div>
					<span class="text-xs text-text-secondary"><strong>DATA MANAGEMENT</strong></span>
					<div class="divider"></div>
				</div>
				<div class="mt-4">
					<div class="flex flex-col gap-2">
						<span class="text-sm">Production Data Reset</span>
						<span class="text-xs text-[var(--text-secondary)]"
							>Clear cached production data and reconnect to AlKamel</span
						>
						<button
							class="btn-primary mt-2"
							[disabled]="isResettingProdData()"
							(click)="onResetProdDataClicked()"
						>
							<i class="bi bi-arrow-clockwise mr-2"></i>
							<span>{{ isResettingProdData() ? 'Resetting...' : 'Reset Production Data' }}</span>
						</button>
					</div>
				</div>
				<div class="flex items-center gap-4 mt-4">
					<div class="divider"></div>
					<span class="text-xs text-text-secondary"><strong>UPLOAD CONFIG</strong></span>
					<div class="divider"></div>
				</div>

				<div
					class="upload-zone mt-4"
					[class.drag-over]="isDragging()"
					(dragover)="onDragOver($event)"
					(dragleave)="onDragLeave($event)"
					(drop)="onDrop($event)"
					(click)="browseFile()"
				>
					<input #fileInput type="file" accept=".json" hidden (change)="onFileSelected($event)" />
					<i class="bi bi-upload text-5xl"></i>
					<p class="upload-title">Drag &amp; drop your JSON file here</p>
					<p class="upload-subtitle">
						or <span class="upload-browse-link">browse files</span> — .json up to 10 MB
					</p>
					@if (uploadedFileName()) {
						<p class="upload-success-text">✓ {{ uploadedFileName() }}</p>
					}
				</div>
				@if (uploadError()) {
					<p class="upload-error-text mt-2">{{ uploadError() }}</p>
				}
				@if (expectedJsonStructure()) {
					<button
						class="btn-clear-data mt-3"
						[disabled]="isUploadSaving()"
						(click)="clearStreamingData()"
					>
						<i class="bi bi-trash"></i>
						Clear uploaded data
					</button>
				}
				@if (!expectedJsonStructure()) {
					<div class="json-structure mt-4">
						<div class="json-structure-label">EXPECTED JSON STRUCTURE</div>
						<pre class="json-structure-code">
&#123;
		"id": "1",
		"teamName": "team-name",
		"carNumber": "30",
		"carDeviceNumber": "2342",
		"rtmpUrl": "rtmp://...",
		"rtmpStreamKey": "key",
		"eip": "1.2.3.4",
		"eipAllocationId": "eipalloc-...",
		"videoId": "video-id",
		"hlsPlaybackUrl": "https://...",
		"isLive": true
		&#125;</pre
						>
					</div>
				}
			}
		</section>
		<!-- /STREAM PANEL -->

		<!-- ADMIN PANEL -->
		<section class="admin-panel">
			<h2 class="card-title flex items-center gap-2">
				<div class="header-icon">
					<i class="bi bi-bell"></i>
				</div>
				Notification Panel
			</h2>
			<div class="divider mb-3"></div>
			<div class="flex mb-3">
				<span class="text-xs status-text"><strong>COMPOSE MESSAGE</strong></span>
				<div class="grow-1"></div>
				<span class="status-text">{{ notificationMessageLength() }} / 300</span>
			</div>
			<textarea
				[ngModel]="notificationMessageDraft()"
				(ngModelChange)="onNotificationMessageChanged($event)"
				class="message-textarea mb-3"
				maxlength="300"
				placeholder="Type your notification message to broadcast to the live site…"
			></textarea>
			<div class="flex gap-2">
				<button
					class="btn-primary grow-1"
					[disabled]="!canSendNotification()"
					(click)="onSendToSiteClicked()"
				>
					<i class="bi bi-send mr-2"></i>
					<span>Send to Site</span>
				</button>
				<button
					class="btn-outline"
					[disabled]="isNotificationSaving()"
					(click)="onClearBannerMessageClicked()"
				>
					Clear
				</button>
			</div>
		</section>
		<!-- /ADMIN PANEL -->
	</div>
	<!-- /admin-panels-row -->

	@if (expectedJsonStructure(); as streamingRows) {
		<vb-lxp-streaming-table [rows]="streamingRows"></vb-lxp-streaming-table>
	}

	<div class="toast" [ngClass]="[showToast() ? 'toast-enter' : '']">
		<i class="bi bi-info-circle"></i>
		<span>{{ toastMessage() }}</span>
	</div>

	<!-- Reset Confirmation Modal -->
	@if (showResetConfirmModal()) {
		<div class="modal-overlay" (click)="onCancelResetProdData()">
			<div class="modal-container" (click)="$event.stopPropagation()">
				<div class="modal-header">
					<i class="bi bi-exclamation-circle text-3xl text-primary"></i>
					<h3 class="modal-title">Reset Production Data?</h3>
					<button
						type="button"
						class="modal-close"
						(click)="onCancelResetProdData()"
						aria-label="Close modal"
					>
						<i class="bi bi-x-lg"></i>
					</button>
				</div>
				<div class="modal-body">
					<p class="modal-description">This action will perform the following operations:</p>
					<ul class="modal-list">
						<li>
							<i class="bi bi-check-circle text-accent-green"></i>
							Clear all cached production data
						</li>
						<li>
							<i class="bi bi-check-circle text-accent-green"></i>
							Disconnect from AlKamel server
						</li>
						<li>
							<i class="bi bi-check-circle text-accent-green"></i>
							Reconnect and fetch fresh data
						</li>
					</ul>
					<p class="modal-warning">This operation cannot be undone.</p>
				</div>
				<div class="modal-footer">
					<button type="button" class="modal-btn-secondary" (click)="onCancelResetProdData()">
						Cancel
					</button>
					<button type="button" class="modal-btn-primary" (click)="onConfirmResetProdData()">
						<i class="bi bi-arrow-clockwise mr-2"></i>
						Reset Production Data
					</button>
				</div>
			</div>
		</div>
	}
</div>
